import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Mail, Download, Users, Calendar, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { Input } from '@/components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminNewsletter = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API_URL}/api/newsletter/subscribers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscribers(response.data);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      toast.error('Failed to load subscribers');
    }
    setLoading(false);
  };

  const handleDelete = async (email) => {
    if (!window.confirm(`Are you sure you want to remove ${email} from the newsletter?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API_URL}/api/newsletter/unsubscribe/${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Subscriber removed');
      fetchSubscribers();
    } catch (error) {
      console.error('Error removing subscriber:', error);
      toast.error('Failed to remove subscriber');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportToCSV = () => {
    const headers = ['Email', 'Subscribed At'];
    const csvContent = [
      headers.join(','),
      ...subscribers.map(sub => `${sub.email},${sub.subscribed_at}`)
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `newsletter_subscribers_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Subscribers exported to CSV');
  };

  const filteredSubscribers = subscribers.filter(sub =>
    sub.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Stats
  const totalSubscribers = subscribers.length;
  const thisMonth = subscribers.filter(sub => {
    const subDate = new Date(sub.subscribed_at);
    const now = new Date();
    return subDate.getMonth() === now.getMonth() && subDate.getFullYear() === now.getFullYear();
  }).length;
  const thisWeek = subscribers.filter(sub => {
    const subDate = new Date(sub.subscribed_at);
    const now = new Date();
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return subDate >= weekAgo;
  }).length;

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl text-[#2C2C2C] mb-2" data-testid="newsletter-title">
              Newsletter Subscribers
            </h1>
            <p className="font-body text-[#767C91]">
              Manage your newsletter mailing list
            </p>
          </div>
          <button
            onClick={exportToCSV}
            disabled={subscribers.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-[#9D6E6D] text-white font-label text-xs tracking-widest hover:bg-[#2C2C2C] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4" />
            EXPORT CSV
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white border border-[#E2E8F0] p-6" data-testid="stat-total">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#F2E1E1] flex items-center justify-center">
                <Users className="w-6 h-6 text-[#9D6E6D]" />
              </div>
              <div>
                <p className="font-label text-xs text-[#767C91] tracking-widest">TOTAL SUBSCRIBERS</p>
                <p className="font-heading text-3xl text-[#2C2C2C]">{totalSubscribers}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-[#E2E8F0] p-6" data-testid="stat-month">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#ABBDDF] flex items-center justify-center">
                <Calendar className="w-6 h-6 text-[#2C2C2C]" />
              </div>
              <div>
                <p className="font-label text-xs text-[#767C91] tracking-widest">THIS MONTH</p>
                <p className="font-heading text-3xl text-[#2C2C2C]">{thisMonth}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-[#E2E8F0] p-6" data-testid="stat-week">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-[#DBC8CE] flex items-center justify-center">
                <Mail className="w-6 h-6 text-[#2C2C2C]" />
              </div>
              <div>
                <p className="font-label text-xs text-[#767C91] tracking-widest">THIS WEEK</p>
                <p className="font-heading text-3xl text-[#2C2C2C]">{thisWeek}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white border border-[#E2E8F0] p-6 mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#767C91]" />
            <Input
              type="text"
              placeholder="Search subscribers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 border-[#E2E8F0] focus:border-[#9D6E6D]"
              data-testid="search-subscribers"
            />
          </div>
        </div>

        {/* Subscribers Table */}
        <div className="bg-white border border-[#E2E8F0] overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-2 border-[#9D6E6D] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="font-body text-[#767C91]">Loading subscribers...</p>
            </div>
          ) : filteredSubscribers.length === 0 ? (
            <div className="p-8 text-center">
              <Mail className="w-12 h-12 text-[#E2E8F0] mx-auto mb-4" />
              <p className="font-body text-[#767C91]">
                {searchQuery ? 'No subscribers match your search' : 'No subscribers yet'}
              </p>
            </div>
          ) : (
            <table className="w-full" data-testid="subscribers-table">
              <thead className="bg-[#F8F9FA] border-b border-[#E2E8F0]">
                <tr>
                  <th className="text-left px-6 py-4 font-label text-xs text-[#767C91] tracking-widest">
                    EMAIL
                  </th>
                  <th className="text-left px-6 py-4 font-label text-xs text-[#767C91] tracking-widest">
                    SUBSCRIBED AT
                  </th>
                  <th className="text-right px-6 py-4 font-label text-xs text-[#767C91] tracking-widest">
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredSubscribers.map((subscriber, index) => (
                  <tr 
                    key={subscriber.email} 
                    className={`border-b border-[#E2E8F0] hover:bg-[#F8F9FA] transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-[#FAFAFA]'}`}
                    data-testid={`subscriber-row-${index}`}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-[#F2E1E1] rounded-full flex items-center justify-center">
                          <span className="font-label text-xs text-[#9D6E6D]">
                            {subscriber.email.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="font-body text-[#2C2C2C]">{subscriber.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-body text-[#767C91] text-sm">
                        {formatDate(subscriber.subscribed_at)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleDelete(subscriber.email)}
                        className="text-[#767C91] hover:text-red-500 transition-colors p-2"
                        title="Remove subscriber"
                        data-testid={`delete-subscriber-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer info */}
        {filteredSubscribers.length > 0 && (
          <p className="mt-4 text-center font-body text-sm text-[#767C91]">
            Showing {filteredSubscribers.length} of {totalSubscribers} subscribers
          </p>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminNewsletter;
